import React, { useState, useEffect } from 'react';
import styles from './UtcDateTimeField.module.scss';
import {
  displayToEpoch,
  validateUtcDisplay,
  formatUtcInputWhileTyping,
} from '../../../lib/datetime';

export interface UtcDateTimeFieldProps {
  value: string;
  onChange: (display: string, epoch: number | null) => void;
  minEpoch?: number;
  maxEpoch?: number;
  error?: string;
  disabled?: boolean;
}

const UtcDateTimeField: React.FC<UtcDateTimeFieldProps> = ({
  value,
  onChange,
  minEpoch,
  maxEpoch,
  error: externalError,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [localError, setLocalError] = useState('');
  const error = externalError || localError;

  // Sync value prop to internal state
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Format while typing (e.g. 20260706 -> 2026-07-06)
    const formatted = formatUtcInputWhileTyping(raw);
    setInputValue(formatted);

    // Validate the display string
    const validationError = validateUtcDisplay(formatted);
    if (validationError) {
      setLocalError(validationError);
      onChange(formatted, null);
    } else {
      const epoch = displayToEpoch(formatted);
      if (epoch === null) {
        setLocalError('Invalid date and time');
        onChange(formatted, null);
      } else if (minEpoch != null && epoch < minEpoch) {
        setLocalError('Must be after start time');
        onChange(formatted, null);
      } else if (maxEpoch != null && epoch > maxEpoch) {
        setLocalError('Out of allowed range');
        onChange(formatted, null);
      } else {
        setLocalError('');
        onChange(formatted, epoch);
      }
    }
  };

  return (
    <div className={styles.wrap} title={error || undefined}>
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder="YYYY-MM-DD HH:mm:ss"
        className={styles.manualInput}
        aria-invalid={!!error}
      />
    </div>
  );
};

export default UtcDateTimeField;
